import { Modal, ModalBody, ModalTitle, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { useMutation } from '@tanstack/react-query';
import { client } from '../api';
import { useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LetterEditorInput } from './tableItemDetailViewComponents/FormComponents';

type EditLetterResponse = {
  id: string;
  document_id: string;
  editable_letter_content: string;
};

type LetterOfMedicalNecessityEditModalProps = {
  paReviewId: string;
  lmnId: string;
  letterContent: string;
  closeModal: () => void;
  setDocumentId: (documentId: string) => void;
  setEditableContent: (editableContent: string) => void;
};

/**
 * Process letter content from the backend.
 * Replace any double <br> with a double newline.
 */
const formatInputForDisplay = (input: string) => {
  return input.replace(/<br><br>/g, '\n\n');
};
/**
 * Process letter content for submission to the backend.
 * Replace double newlines with a double <br>.
 */
const formatInputForSubmission = (input: string) => {
  return input.replace(/\n\n/g, '<br><br>');
};

const LetterOfMedicalNecessityEditModal = ({
  paReviewId,
  lmnId,
  letterContent,
  closeModal,
  setDocumentId,
  setEditableContent,
}: LetterOfMedicalNecessityEditModalProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const initialInputValue = formatInputForDisplay(letterContent);
  const [inputValue, setInputValue] = useState<string>(initialInputValue);

  const regenerate = useMutation<EditLetterResponse, Error, void>({
    mutationFn: async () => {
      const data = {
        editable_letter_content: formatInputForSubmission(inputValue),
      };

      const response = await client.patch(
        `prior-auth-reviews/${paReviewId}/letter-of-medical-necessity/${lmnId}`,
        data,
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.custom(() => <Toast variant="success">Letter of Medical Necessity updated successfully.</Toast>);
      // Set the document ID in PA review state so it refreshes the preview
      setDocumentId(data.document_id);
      // Set editable content in case they want to edit again
      setEditableContent(data.editable_letter_content);
      closeModal();
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      Sentry.captureException(error);
    },
  });

  return (
    <Modal onClose={closeModal}>
      <ModalTitle>Edit Letter of Medical Necessity</ModalTitle>

      <ModalBody>
        <Typography>
          Please note that this text will be inserted into the letter template shown on the page. You can save and
          preview the final PDF before submitting.
        </Typography>
        <LetterEditorInput
          ref={inputRef}
          placeholder="Update letter content..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" disabled={regenerate.isPending} onClick={closeModal}>
          Cancel
        </Button>
        <Button onClick={() => regenerate.mutate()} isLoading={regenerate.isPending} disabled={!inputValue.trim()}>
          Save & preview PDF
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default LetterOfMedicalNecessityEditModal;
